import React, { useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Tab,
	Tabs,
	Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePresentationDraftSource } from "../../../hooks/usePresentationDrafts";
import { zoullooColors } from "../../../theme";

interface TranscriptDialogProps {
	readonly open: boolean;
	readonly draftId: number;
	readonly onClose: () => void;
}

const TranscriptDialog: React.FC<TranscriptDialogProps> = ({ open, draftId, onClose }) => {
	const { t } = useTranslation();
	const { data: source, isLoading, isError } = usePresentationDraftSource(draftId, open);
	const [tab, setTab] = useState(0);

	const hasFirstInterview = !!source?.firstInterviewTranscript;
	const transcript = tab === 1 && hasFirstInterview ? source?.firstInterviewTranscript : source?.secondInterviewTranscript;

	const renderContent = () => {
		if (isLoading) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress color="secondary" />
				</Box>
			);
		}

		if (isError || !source) {
			return (
				<Typography variant="body2" color="error">
					{t("presentationDraft.transcript.error")}
				</Typography>
			);
		}

		return (
			<Box
				sx={{
					whiteSpace: "pre-wrap",
					fontSize: "14px",
					lineHeight: 1.6,
					backgroundColor: zoullooColors.grey[25],
					border: `1px solid ${zoullooColors.grey[300]}`,
					borderRadius: "8px",
					p: 2
				}}
			>
				{transcript}
			</Box>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
			<DialogTitle>{t("presentationDraft.transcript.title")}</DialogTitle>
			{hasFirstInterview && (
				<Tabs value={tab} onChange={(_, value: number) => setTab(value)} sx={{ px: 3 }} textColor="secondary" indicatorColor="secondary">
					<Tab label={t("presentationDraft.transcript.secondInterview")} />
					<Tab label={t("presentationDraft.transcript.firstInterview")} />
				</Tabs>
			)}
			<DialogContent dividers>{renderContent()}</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t("common.close")}</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TranscriptDialog;
